/**
 * Expense Settings Page
 * Manage Expense Heads, Currency Rates, Country Config, and Allocation Rules
 */
import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, toast } from "../App";
import {
  Plus,
  Edit,
  Trash2,
  Settings,
  DollarSign,
  Globe,
  Percent,
  RefreshCw,
} from "lucide-react";

export default function ExpenseSettings({ user }) {
  const [activeTab, setActiveTab] = useState("heads");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [expenseHeads, setExpenseHeads] = useState([]);
  const [currencyRates, setCurrencyRates] = useState([]);
  const [countryConfigs, setCountryConfigs] = useState([]);
  const [allocationRules, setAllocationRules] = useState([]);
  const [branches, setBranches] = useState([]);
  
  // Dialog states
  const [showHeadDialog, setShowHeadDialog] = useState(false);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [showCountryDialog, setShowCountryDialog] = useState(false);
  const [showAllocationDialog, setShowAllocationDialog] = useState(false);
  
  // Edit states
  const [editingHead, setEditingHead] = useState(null);
  const [editingRate, setEditingRate] = useState(null);
  const [editingCountry, setEditingCountry] = useState(null);
  const [editingAllocation, setEditingAllocation] = useState(null);
  
  // Form states
  const [headForm, setHeadForm] = useState({ code: "", name: "", category: "Personnel", expense_type: "Direct", description: "", is_active: true });
  const [rateForm, setRateForm] = useState({ from_currency: "INR", to_currency: "USD", rate: "", effective_date: new Date().toISOString().split('T')[0] });
  const [countryForm, setCountryForm] = useState({ country: "", currency: "USD", working_hours_per_month: 160, working_days_per_month: 20 });
  const [allocationForm, setAllocationForm] = useState({ branch: "", department: "", allocation_percentage: "", effective_from: new Date().toISOString().split('T')[0] });

  const departments = ["Projects", "Management", "Sales", "HR", "Finance", "Admin"];
  const categories = ["Personnel", "Operations", "Equipment", "Admin"];
  const currencies = ["USD", "INR", "CAD", "EUR", "GBP"];

  useEffect(() => {
    fetchAllData();
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await api.get("/branches");
      setBranches(response.data || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [headsRes, ratesRes, configsRes, rulesRes] = await Promise.all([
        api.get("/expense-heads"),
        api.get("/currency-rates"),
        api.get("/country-config"),
        api.get("/allocation-rules"),
      ]);
      setExpenseHeads(headsRes.data);
      setCurrencyRates(ratesRes.data);
      setCountryConfigs(configsRes.data);
      setAllocationRules(rulesRes.data);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const response = await api.post("/expenses/seed-defaults");
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to seed defaults");
    }
  };

  // ==================== EXPENSE HEADS ====================
  const handleSaveHead = async () => {
    try {
      if (editingHead) {
        await api.put(`/expense-heads/${editingHead.id}`, headForm);
        toast.success("Expense head updated");
      } else {
        await api.post("/expense-heads", headForm);
        toast.success("Expense head created");
      }
      setShowHeadDialog(false);
      resetHeadForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleEditHead = (head) => {
    setEditingHead(head);
    setHeadForm({
      code: head.code,
      name: head.name,
      category: head.category,
      expense_type: head.expense_type,
      description: head.description || "",
      is_active: head.is_active
    });
    setShowHeadDialog(true);
  };

  const handleDeleteHead = async (id) => {
    if (!confirm("Are you sure?")) return;
    try {
      await api.delete(`/expense-heads/${id}`);
      toast.success("Expense head deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const resetHeadForm = () => {
    setEditingHead(null);
    setHeadForm({ code: "", name: "", category: "Personnel", expense_type: "Direct", description: "", is_active: true });
  };

  // ==================== CURRENCY RATES ====================
  const handleSaveRate = async () => {
    try {
      if (editingRate) {
        await api.put(`/currency-rates/${editingRate.id}`, rateForm);
        toast.success("Currency rate updated");
      } else {
        await api.post("/currency-rates", rateForm);
        toast.success("Currency rate created");
      }
      setShowRateDialog(false);
      resetRateForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleEditRate = (rate) => {
    setEditingRate(rate);
    setRateForm({
      from_currency: rate.from_currency,
      to_currency: rate.to_currency,
      rate: rate.rate,
      effective_date: rate.effective_date
    });
    setShowRateDialog(true);
  };

  const handleDeleteRate = async (id) => {
    if (!confirm("Are you sure?")) return;
    try {
      await api.delete(`/currency-rates/${id}`);
      toast.success("Currency rate deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const resetRateForm = () => {
    setEditingRate(null);
    setRateForm({ from_currency: "INR", to_currency: "USD", rate: "", effective_date: new Date().toISOString().split('T')[0] });
  };

  // ==================== COUNTRY CONFIG ====================
  const handleSaveCountry = async () => {
    try {
      if (editingCountry) {
        await api.put(`/country-config/${editingCountry.id}`, countryForm);
        toast.success("Country config updated");
      } else {
        await api.post("/country-config", countryForm);
        toast.success("Country config created");
      }
      setShowCountryDialog(false);
      resetCountryForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleEditCountry = (config) => {
    setEditingCountry(config);
    setCountryForm({
      country: config.country,
      currency: config.currency,
      working_hours_per_month: config.working_hours_per_month,
      working_days_per_month: config.working_days_per_month
    });
    setShowCountryDialog(true);
  };

  const resetCountryForm = () => {
    setEditingCountry(null);
    setCountryForm({ country: "", currency: "USD", working_hours_per_month: 160, working_days_per_month: 20 });
  };

  // ==================== ALLOCATION RULES ====================
  const handleSaveAllocation = async () => {
    try {
      if (editingAllocation) {
        await api.put(`/allocation-rules/${editingAllocation.id}`, allocationForm);
        toast.success("Allocation rule updated");
      } else {
        await api.post("/allocation-rules", allocationForm);
        toast.success("Allocation rule created");
      }
      setShowAllocationDialog(false);
      resetAllocationForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleEditAllocation = (rule) => {
    setEditingAllocation(rule);
    setAllocationForm({
      branch: rule.branch,
      department: rule.department,
      allocation_percentage: rule.allocation_percentage,
      effective_from: rule.effective_from
    });
    setShowAllocationDialog(true);
  };

  const handleDeleteAllocation = async (id) => {
    if (!confirm("Are you sure?")) return;
    try {
      await api.delete(`/allocation-rules/${id}`);
      toast.success("Allocation rule deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const resetAllocationForm = () => {
    setEditingAllocation(null);
    setAllocationForm({ branch: "", department: "", allocation_percentage: "", effective_from: new Date().toISOString().split('T')[0] });
  };

  const canManage = user?.access_level === "Manager" || user?.access_level === "Admin";

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Expense Settings</h1>
            <p className="text-gray-500">Configure expense heads, currency rates, and allocation rules</p>
          </div>
          {user?.access_level === "Admin" && (
            <Button variant="outline" onClick={handleSeedDefaults}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Seed Defaults
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="heads">
              <Settings className="h-4 w-4 mr-2" />
              Expense Heads
            </TabsTrigger>
            <TabsTrigger value="rates">
              <DollarSign className="h-4 w-4 mr-2" />
              Currency Rates
            </TabsTrigger>
            <TabsTrigger value="countries">
              <Globe className="h-4 w-4 mr-2" />
              Country Config
            </TabsTrigger>
            <TabsTrigger value="allocation">
              <Percent className="h-4 w-4 mr-2" />
              Allocation Rules
            </TabsTrigger>
          </TabsList>

          {/* Expense Heads Tab */}
          <TabsContent value="heads">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Expense Heads</CardTitle>
                {canManage && (
                  <Button onClick={() => { resetHeadForm(); setShowHeadDialog(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Head
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      {canManage && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenseHeads.map((head) => (
                      <TableRow key={head.id}>
                        <TableCell className="font-mono">{head.code}</TableCell>
                        <TableCell className="font-medium">{head.name}</TableCell>
                        <TableCell>{head.category}</TableCell>
                        <TableCell>
                          <Badge className={head.expense_type === "Direct" ? "bg-green-100 text-green-800" : "bg-purple-100 text-purple-800"}>
                            {head.expense_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={head.is_active ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"}>
                            {head.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        {canManage && (
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEditHead(head)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              {user?.access_level === "Admin" && (
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteHead(head.id)} className="text-red-600">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Currency Rates Tab */}
          <TabsContent value="rates">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Currency Conversion Rates</CardTitle>
                {canManage && (
                  <Button onClick={() => { resetRateForm(); setShowRateDialog(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Rate
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Effective Date</TableHead>
                      {canManage && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currencyRates.map((rate) => (
                      <TableRow key={rate.id}>
                        <TableCell className="font-mono font-bold">{rate.from_currency}</TableCell>
                        <TableCell className="font-mono">{rate.to_currency}</TableCell>
                        <TableCell className="font-mono text-green-600">{rate.rate}</TableCell>
                        <TableCell>{rate.effective_date}</TableCell>
                        {canManage && (
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEditRate(rate)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              {user?.access_level === "Admin" && (
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteRate(rate.id)} className="text-red-600">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Country Config Tab */}
          <TabsContent value="countries">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Country Configuration</CardTitle>
                {canManage && (
                  <Button onClick={() => { resetCountryForm(); setShowCountryDialog(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Country
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Country</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead>Working Hours/Month</TableHead>
                      <TableHead>Working Days/Month</TableHead>
                      {canManage && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {countryConfigs.map((config) => (
                      <TableRow key={config.id}>
                        <TableCell className="font-medium">{config.country}</TableCell>
                        <TableCell className="font-mono">{config.currency}</TableCell>
                        <TableCell>{config.working_hours_per_month}h</TableCell>
                        <TableCell>{config.working_days_per_month} days</TableCell>
                        {canManage && (
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => handleEditCountry(config)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Allocation Rules Tab */}
          <TabsContent value="allocation">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Department Allocation Rules</CardTitle>
                {canManage && (
                  <Button onClick={() => { resetAllocationForm(); setShowAllocationDialog(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Rule
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Allocation percentages define how indirect costs (rent, utilities, etc.) are distributed to each department. 
                    The total percentage for all departments in a branch should equal 100%.
                  </p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Branch</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Allocation %</TableHead>
                      <TableHead>Effective From</TableHead>
                      {canManage && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allocationRules.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                          No allocation rules defined. Add rules to enable indirect cost allocation.
                        </TableCell>
                      </TableRow>
                    ) : (
                      allocationRules.map((rule) => (
                        <TableRow key={rule.id}>
                          <TableCell className="font-medium">{rule.branch}</TableCell>
                          <TableCell>{rule.department}</TableCell>
                          <TableCell className="font-mono text-purple-600">{rule.allocation_percentage}%</TableCell>
                          <TableCell>{rule.effective_from}</TableCell>
                          {canManage && (
                            <TableCell>
                              <div className="flex gap-2">
                                <Button variant="ghost" size="sm" onClick={() => handleEditAllocation(rule)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {user?.access_level === "Admin" && (
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteAllocation(rule.id)} className="text-red-600">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Expense Head Dialog */}
        <Dialog open={showHeadDialog} onOpenChange={setShowHeadDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingHead ? "Edit Expense Head" : "Add Expense Head"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Code *</Label>
                  <Input value={headForm.code} onChange={(e) => setHeadForm({...headForm, code: e.target.value.toUpperCase()})} placeholder="SAL001" />
                </div>
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={headForm.name} onChange={(e) => setHeadForm({...headForm, name: e.target.value})} placeholder="Basic Salary" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={headForm.category} onValueChange={(v) => setHeadForm({...headForm, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={headForm.expense_type} onValueChange={(v) => setHeadForm({...headForm, expense_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Direct">Direct (Employee/Dept specific)</SelectItem>
                      <SelectItem value="Indirect">Indirect (Allocated)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={headForm.description} onChange={(e) => setHeadForm({...headForm, description: e.target.value})} placeholder="Optional description" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHeadDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveHead}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Currency Rate Dialog */}
        <Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingRate ? "Edit Currency Rate" : "Add Currency Rate"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Currency *</Label>
                  <Select value={rateForm.from_currency} onValueChange={(v) => setRateForm({...rateForm, from_currency: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>To Currency</Label>
                  <Input value="USD" disabled className="bg-gray-100" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rate *</Label>
                  <Input type="number" step="0.0001" value={rateForm.rate} onChange={(e) => setRateForm({...rateForm, rate: parseFloat(e.target.value) || ""})} placeholder="0.012" />
                </div>
                <div className="space-y-2">
                  <Label>Effective Date *</Label>
                  <Input type="date" value={rateForm.effective_date} onChange={(e) => setRateForm({...rateForm, effective_date: e.target.value})} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRateDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveRate}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Country Config Dialog */}
        <Dialog open={showCountryDialog} onOpenChange={setShowCountryDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCountry ? "Edit Country Config" : "Add Country Config"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Country *</Label>
                  <Input value={countryForm.country} onChange={(e) => setCountryForm({...countryForm, country: e.target.value})} placeholder="India" />
                </div>
                <div className="space-y-2">
                  <Label>Currency *</Label>
                  <Select value={countryForm.currency} onValueChange={(v) => setCountryForm({...countryForm, currency: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Working Hours/Month *</Label>
                  <Input type="number" value={countryForm.working_hours_per_month} onChange={(e) => setCountryForm({...countryForm, working_hours_per_month: parseFloat(e.target.value) || 0})} />
                </div>
                <div className="space-y-2">
                  <Label>Working Days/Month *</Label>
                  <Input type="number" value={countryForm.working_days_per_month} onChange={(e) => setCountryForm({...countryForm, working_days_per_month: parseInt(e.target.value) || 0})} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCountryDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveCountry}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Allocation Rule Dialog */}
        <Dialog open={showAllocationDialog} onOpenChange={setShowAllocationDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingAllocation ? "Edit Allocation Rule" : "Add Allocation Rule"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Branch *</Label>
                  <Select value={allocationForm.branch} onValueChange={(v) => setAllocationForm({...allocationForm, branch: v})}>
                    <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>
                      {branches.map(b => <SelectItem key={b.id} value={b.name}>{b.name} ({b.country})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Department *</Label>
                  <Select value={allocationForm.department} onValueChange={(v) => setAllocationForm({...allocationForm, department: v})}>
                    <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Allocation % *</Label>
                  <Input type="number" step="0.1" value={allocationForm.allocation_percentage} onChange={(e) => setAllocationForm({...allocationForm, allocation_percentage: parseFloat(e.target.value) || ""})} placeholder="25" />
                </div>
                <div className="space-y-2">
                  <Label>Effective From *</Label>
                  <Input type="date" value={allocationForm.effective_from} onChange={(e) => setAllocationForm({...allocationForm, effective_from: e.target.value})} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAllocationDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveAllocation}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
